/**
 * Native push notification service for Capacitor (Android/iOS)
 * Falls back to web Firebase messaging when running in browser
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const isNative = Capacitor.isNativePlatform();

/**
 * Check the current OS-level notification permission *without* triggering
 * the system prompt. Returns one of: 'granted', 'denied', 'prompt',
 * 'prompt-with-rationale' (Android only), or null if not on native.
 *
 * Used by AuthContext to decide whether to silently re-register an FCM
 * token (when granted but our DB has none — typical of users who said yes
 * months ago but lost the token to a previous app glitch / re-install).
 */
export const checkNativeNotificationPermission = async () => {
  if (!isNative) return null;
  try {
    const result = await PushNotifications.checkPermissions();
    return result?.receive || null;
  } catch (e) {
    console.warn('checkNativeNotificationPermission failed:', e?.message || e);
    return null;
  }
};

/**
 * Silently fetch a fresh push token from the OS — only valid when the
 * permission is already granted. Does NOT show the OS prompt.
 */
export const fetchNativeTokenSilently = async () => {
  if (!isNative) return null;
  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm?.receive !== 'granted') return null;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 12000);
      PushNotifications.addListener('registration', (token) => {
        clearTimeout(timeout);
        resolve(token.value || null);
      });
      PushNotifications.addListener('registrationError', () => {
        clearTimeout(timeout);
        resolve(null);
      });
      PushNotifications.register().catch(() => {
        clearTimeout(timeout);
        resolve(null);
      });
    });
  } catch (e) {
    console.warn('fetchNativeTokenSilently failed:', e?.message || e);
    return null;
  }
};

/**
 * Request push notification permission and get token.
 * Uses Capacitor PushNotifications on native, Firebase Web on browser.
 */
export const requestNativeNotificationPermission = async () => {
  if (!isNative) {
    // Fallback to web Firebase - but only if Notification API is available
    if (typeof Notification === 'undefined') return null;
    const { requestNotificationPermission } = await import('../firebase');
    return requestNotificationPermission();
  }

  try {
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Native push permission denied');
      return null;
    }

    // IMPORTANT: Add listeners BEFORE calling register() to avoid race condition
    // where the registration event fires before the listener is attached
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn('Native push registration timed out after 15s');
        resolve(null);
      }, 15000);

      PushNotifications.addListener('registration', (token) => {
        clearTimeout(timeout);
        console.log('Native push token received:', token.value?.substring(0, 20) + '...');
        resolve(token.value);
      });

      PushNotifications.addListener('registrationError', (err) => {
        clearTimeout(timeout);
        console.error('Native push registration error:', JSON.stringify(err));
        resolve(null);
      });

      // Call register AFTER listeners are set up
      PushNotifications.register().catch((err) => {
        clearTimeout(timeout);
        console.error('Native push register() failed:', err);
        resolve(null);
      });
    });
  } catch (error) {
    console.error('Error requesting native push permission:', error);
    return null;
  }
};

/**
 * Set up listeners for incoming push notifications (native only)
 */
export const setupNativeNotificationListeners = (onNotification) => {
  if (!isNative) return () => {};

  // Notification received while app is in foreground
  const foregroundListener = PushNotifications.addListener(
    'pushNotificationReceived',
    (notification) => {
      console.log('Native push received (foreground):', notification);
      if (onNotification) {
        onNotification(notification);
      }
    }
  );

  // NOTE: `pushNotificationActionPerformed` (notification tap) is handled
  // exclusively in `src/index.js` so the same logic applies to both warm and
  // cold-start. Registering a second listener here would cause double-open of
  // external URLs.

  return () => {
    foregroundListener.then(l => l.remove());
  };
};

/**
 * Check if running in native Capacitor environment
 */
export const isNativePlatform = () => isNative;

/**
 * Get the current platform name
 */
export const getPlatform = () => Capacitor.getPlatform();
